import { DialogActions, DialogContent, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { getDocumentsAvailableForReplacement } from 'lib/api/reSell/factoring';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import Table from 'lib/designSystem/Table';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { DocumentReplacementData, DocumentReplacementProps } from './types';

const DocumentEditor: React.FC<DocumentReplacementProps> = ({
  open,
  setOpen,
  currentDocument: _currentDocument,
  onDocumentReplace,
  canReplace: _canReplace,
  resellId,
}) => {
  const dispatch = useDispatch();
  const [searchLoading, setSearchLoading] = useState(false);
  const [replacingDocument, setReplacingDocument] = useState(false);
  const [documents, setDocuments] = useState<DocumentReplacementData[]>([]);
  const [searched, setSearched] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [cachedDocuments, setCachedDocuments] = useState<
    DocumentReplacementData[]
  >([]);

  // Filters
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [folio, setFolio] = useState('');
  const [requestId, setRequestId] = useState('');
  const [businessName, setBusinessName] = useState('');

  const filters: Filter[] = [
    {
      label: 'Nombre deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut contraparte',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Solicitud',
      type: 'text',
      value: requestId,
      setter: setRequestId,
    },
    {
      label: 'Cliente',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
  ];

  const handleSetSelected = (selectedIds: string[]) => {
    const newSelectedIds = selectedIds.slice(-1);

    const newCachedDocuments = [...cachedDocuments];
    for (const id of newSelectedIds) {
      const document = documents.find(
        (document: DocumentReplacementData) => document.id === id,
      );
      if (document) {
        const newCachedIds = [
          ...newCachedDocuments.map(
            (document: DocumentReplacementData) => document.id,
          ),
        ];
        if (!newCachedIds.includes(document.id)) {
          newCachedDocuments.push(document);
        }
      }
    }
    setCachedDocuments(newCachedDocuments);
    setSelectedIds(newSelectedIds);
  };

  const searchDocuments = async () => {
    try {
      let query = '';
      if (folio) query += `folio=${folio}&`;
      if (stakeholderIdentifier)
        query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
      if (stakeholderName) query += `stakeholderName=${stakeholderName}&`;
      if (requestId) query += `requestId=${requestId}&`;
      if (businessName) query += `businessName=${businessName}&`;

      const response = await getDocumentsAvailableForReplacement(
        dispatch,
        resellId,
        query,
      );

      return response.documents || [];
    } catch (error) {
      console.error('Error fetching documents:', error);
      return [];
    }
  };

  const handleSearchDocuments = async () => {
    try {
      const documents = await searchDocuments();
      setDocuments(documents);
      setCachedDocuments(documents);
      setSearched(true);
      return documents;
    } catch (error) {
      // Error handling
    }
  };

  const triggerSearch = async () => {
    setSearchLoading(true);
    await handleSearchDocuments();
    setSearchLoading(false);
  };

  const handleSelectDocuments = async () => {
    if (selectedIds.length === 0) {
      return;
    }

    const selectedDocument = cachedDocuments.find(
      (document: DocumentReplacementData) => document.id === selectedIds[0],
    );

    if (selectedDocument) {
      setReplacingDocument(true);
      try {
        await onDocumentReplace(selectedDocument.id);
        setOpen(false);
      } catch (error) {
        console.error('Error in handleSelectDocuments:', error);
      } finally {
        setReplacingDocument(false);
      }
    }
  };

  const setup = async () => {
    await handleSearchDocuments();
  };

  useEffect(() => {
    if (open) {
      void setup();
    }
  }, [open]);

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <DialogTitle>
        <HeaderWithActions title="Reemplazo de Documento" type="dialogHeader" />
      </DialogTitle>

      <DialogContent>
        {!searched ? (
          <CenteredCircularProgress type="dialog" />
        ) : (
          <div style={{ padding: '0px 0px 20px 0px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <Typography variant="h6" gutterBottom>
                  Seleccionar documento para la venta
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Estos son los documentos disponibles para remplazar
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedIds.length > 0
                    ? '1 documento seleccionado'
                    : 'Ningún documento seleccionado'}
                </Typography>
              </div>
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerSearch}
                dispatch={dispatch}
                disableSearch={searchLoading}
              />
            </div>
            <div>
              <Table
                dataHeaders={[
                  { key: 'requestType', label: 'Tipo', type: 'text' },
                  {
                    key: 'requestId',
                    label: 'Solicitud',
                    type: 'numeric-id',
                  },
                  { key: 'folio', label: 'Folio' },
                  { key: 'businessName', label: 'Cliente' },
                  { key: 'stakeholderName', label: 'Deudor' },
                  { key: 'amount', label: 'Monto', type: 'money' },
                  {
                    key: 'paymentDate',
                    label: 'Fecha vencimiento',
                    type: 'date',
                  },
                ]}
                data={documents}
                selected={selectedIds}
                setSelected={handleSetSelected}
                hideTablePagination={true}
              />
            </div>
          </div>
        )}
        <DialogActions>
          <CustomButton
            variant="contained"
            color="secondary"
            onClick={() => {
              setOpen(false);
            }}
          >
            {' '}
            Salir
          </CustomButton>
          {searched && (
            <CustomButton
              variant="contained"
              color="primary"
              disabled={!selectedIds.length || replacingDocument}
              loading={replacingDocument}
              onClick={handleSelectDocuments}
            >
              {' '}
              Reemplazar Documento
            </CustomButton>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentEditor;
